import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Bundle, Communication } from 'fhir/r4';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { backendEndPointToken } from '../app.config';
import { DisableCache, LoadingUIEnabled } from '../loading.interceptor';
import {
  MessagingCommunicationSearchContract,
  MessagingCurrentContext,
  MessagingThread,
  MessagingThreadAnchorRef,
  MessagingThreadMessage
} from './messaging.models';

@Injectable({ providedIn: 'root' })
export class MessagingMessagesService {
  private readonly http = inject(HttpClient);
  private readonly backendEndPoint = inject(backendEndPointToken);

  /**
   * Thread-to-Communication contract:
   * 1. threadId itself is not searched against FHIR.
   * 2. Membership is derived from subject + searchable clinical anchors + participant involvement.
   * 3. Only CapabilityStatement-supported params are used for server narrowing: subject, based-on, part-of, sender, recipient.
   * 4. Final inclusion is enforced locally after bundle retrieval.
   */
  buildCommunicationSearchContract(thread: MessagingThread, context?: MessagingCurrentContext): MessagingCommunicationSearchContract {
    const subjectRefs = this.getSubjectRefs(thread);
    const participantRefs = this.getParticipantRefs(thread, context);
    const anchorRefs = this.getAnchorRefs(thread);
    const searches: Array<{ label: string; params: Record<string, string> }> = [];

    if (anchorRefs.length) {
      anchorRefs.forEach((anchor) => {
        searches.push({
          label: `anchor:${anchor.param}:${anchor.reference}`,
          params: {
            ...(subjectRefs[0] ? { subject: subjectRefs[0] } : {}),
            [anchor.param]: anchor.reference,
            _count: '200'
          }
        });
      });
    } else if (subjectRefs[0]) {
      searches.push({
        label: 'subject-only',
        params: {
          subject: subjectRefs[0],
          _count: '200'
        }
      });
    } else if (context?.participantId) {
      const currentParticipantRef = participantRefs.find((ref) => ref.endsWith(`/${context.participantId}`))
        ?? `Practitioner/${context.participantId}`;

      searches.push(
        { label: 'sender-self-fallback', params: { sender: currentParticipantRef, _count: '200' } },
        { label: 'recipient-self-fallback', params: { recipient: currentParticipantRef, _count: '200' } }
      );
    }

    return {
      threadId: thread.threadId,
      threadType: thread.threadType,
      subjectRefs,
      participantRefs,
      anchorRefs,
      searches,
      rules: [
        'Normalize relative and absolute references before matching.',
        'Match thread subject when defined.',
        'Match at least one clinical anchor reference when defined.',
        'Match participant involvement using sender and recipient references against the normalized thread participant set.',
        'Sort resulting Communication resources by sent time ascending for thread display.'
      ]
    };
  }

  listThreadMessages(thread: MessagingThread, context?: MessagingCurrentContext): Observable<MessagingThreadMessage[]> {
    const contract = this.buildCommunicationSearchContract(thread, context);
    if (!contract.searches.length) return of([]);

    return forkJoin(contract.searches.map((search) => this.fetchCommunicationBundle(search.params))).pipe(
      map((bundles) => bundles.flatMap((bundle) => this.extractCommunications(bundle))),
      map((communications) => this.deduplicateCommunications(communications)),
      map((communications) => communications.filter((communication) => this.communicationBelongsToThread(communication, contract))),
      map((communications) => communications.sort((left, right) => this.sortBySent(left, right))),
      map((communications) => communications.map((communication) => this.toMessageViewModel(communication)))
    );
  }

  private fetchCommunicationBundle(params: Record<string, string>): Observable<Bundle> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) httpParams = httpParams.set(key, value);
    });

    const context = new HttpContext().set(DisableCache, true).set(LoadingUIEnabled, false);
    return this.http.get<Bundle>(`${this.backendEndPoint}/Communication`, { params: httpParams, context }).pipe(
      catchError((error) => {
        console.error('Failed to fetch Communication bundle for messaging thread', error);
        return of({ resourceType: 'Bundle', type: 'searchset', entry: [] } as Bundle);
      })
    );
  }

  private extractCommunications(bundle: Bundle | null | undefined): Communication[] {
    return (bundle?.entry ?? [])
      .filter((entry: any) => entry?.search?.mode !== 'outcome')
      .map((entry: any) => entry?.resource)
      .filter((resource: any) => resource?.resourceType === 'Communication');
  }

  private deduplicateCommunications(communications: Communication[]): Communication[] {
    const seen = new Set<string>();
    return communications.filter((communication) => {
      const id = (communication?.id ?? '').toString().trim();
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }

  private communicationBelongsToThread(communication: Communication, contract: MessagingCommunicationSearchContract): boolean {
    const subjectMatches = !contract.subjectRefs.length
      || contract.subjectRefs.includes(this.normalizeReference(communication?.subject?.reference));
    if (!subjectMatches) return false;

    if (contract.anchorRefs.length) {
      const basedOnRefs = (communication?.basedOn ?? []).map((ref: any) => this.normalizeReference(ref?.reference));
      const partOfRefs = (communication?.partOf ?? []).map((ref: any) => this.normalizeReference(ref?.reference));
      const anchorMatches = contract.anchorRefs.some((anchor) => {
        const normalized = this.normalizeReference(anchor.reference);
        return anchor.param === 'based-on'
          ? basedOnRefs.includes(normalized)
          : partOfRefs.includes(normalized);
      });
      if (!anchorMatches) return false;
    }

    if (!contract.participantRefs.length) return true;

    const senderRef = this.normalizeReference((communication?.sender as any)?.reference);
    const recipientRefs = (communication?.recipient ?? []).map((ref: any) => this.normalizeReference(ref?.reference));

    return (!!senderRef && contract.participantRefs.includes(senderRef))
      || recipientRefs.some((ref) => contract.participantRefs.includes(ref));
  }

  private toMessageViewModel(communication: Communication): MessagingThreadMessage {
    const payloads = Array.isArray(communication?.payload) ? communication.payload : [];
    const text = payloads
      .map((payload: any) => payload?.contentString ?? payload?.contentAttachment?.title ?? payload?.contentAttachment?.url)
      .filter(Boolean)
      .join('\n')
      .trim();

    return {
      id: (communication?.id ?? '').toString(),
      communication,
      sentAt: communication?.sent,
      senderDisplay: (communication?.sender as any)?.display ?? this.normalizeReference((communication?.sender as any)?.reference) ?? 'Unknown sender',
      senderReference: this.normalizeReference((communication?.sender as any)?.reference),
      recipientReferences: (communication?.recipient ?? []).map((ref: any) => this.normalizeReference(ref?.reference)).filter(Boolean),
      text: text || '[No text payload]',
      status: communication?.status
    };
  }

  private getSubjectRefs(thread: MessagingThread): string[] {
    const refs = [
      thread?.subject?.resourceType && thread?.subject?.resourceId ? `${thread.subject.resourceType}/${thread.subject.resourceId}` : '',
      thread?.context?.['subjectReference'] ?? ''
    ].filter(Boolean);
    return Array.from(new Set(refs.map((ref) => this.normalizeReference(ref)).filter(Boolean)));
  }

  private getParticipantRefs(thread: MessagingThread, context?: MessagingCurrentContext): string[] {
    const refs = (thread?.participants ?? [])
      .map((participant) => participant?.resourceType && participant?.resourceId
        ? `${participant.resourceType}/${participant.resourceId}`
        : participant?.resourceId
          ? `Practitioner/${participant.resourceId}`
          : participant?.participantId
            ? `Practitioner/${participant.participantId}`
            : '')
      .filter(Boolean);

    if (context?.participantId) refs.push(`Practitioner/${context.participantId}`);

    return Array.from(new Set(refs.map((ref) => this.normalizeReference(ref)).filter(Boolean)));
  }

  private getAnchorRefs(thread: MessagingThread): MessagingThreadAnchorRef[] {
    const context = thread?.context ?? {};
    const anchors: MessagingThreadAnchorRef[] = [];

    if (context.serviceRequestId) {
      anchors.push({ param: 'based-on', reference: `ServiceRequest/${context.serviceRequestId}`, reason: 'service-request-context' });
    }
    if (context.medicationRequestId) {
      anchors.push({ param: 'based-on', reference: `MedicationRequest/${context.medicationRequestId}`, reason: 'medication-request-context' });
    }
    if (context.medicationDispenseId) {
      anchors.push({ param: 'part-of', reference: `MedicationDispense/${context.medicationDispenseId}`, reason: 'medication-dispense-context' });
    }
    if (context.diagnosticReportId) {
      anchors.push({ param: 'part-of', reference: `DiagnosticReport/${context.diagnosticReportId}`, reason: 'diagnostic-report-context' });
    }

    return anchors;
  }

  private normalizeReference(reference: any): string {
    const raw = (reference ?? '').toString().trim();
    if (!raw) return '';
    const noQuery = raw.split('?')[0].split('#')[0];
    const match = noQuery.match(/([A-Za-z]+)\/([^/]+)$/);
    return match ? `${match[1]}/${match[2]}` : noQuery;
  }

  private sortBySent(left: Communication, right: Communication): number {
    const leftTs = new Date((left?.sent ?? (left as any)?.meta?.lastUpdated ?? 0) as any).getTime();
    const rightTs = new Date((right?.sent ?? (right as any)?.meta?.lastUpdated ?? 0) as any).getTime();
    return leftTs - rightTs;
  }
}
